import React, { useState } from "react";
import {
  Dimensions,
  Image,
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useGlobal } from "@/Components/Context/globalContext";
import { playSoundForAWhile } from "@/Components/Functions/MapFunctions";
import constants from "@/config/constants";
import useJocTeTemps from "@/hooks/useJocTeTemps";
import { WebView } from "react-native-webview";
import JocComponent from "@/Components/Components/JocComponent";
import { JigsawPuzzle } from "react-jigsaw-puzzle/lib";
import "react-jigsaw-puzzle/lib/jigsaw-puzzle.css";
import { FontAwesome5 } from "@expo/vector-icons";

export default function JocPuzleJigsaw(RouterData: any) {
  const { __, imatgeRecurs, textRecurs } = useGlobal();
  const [fullscreen, setFullscreen] = useState(false);
  const [showCompletedFullscreen, setShowCompletedFullscreen] = useState(false);
  /** Si la pregunta no és undefined, és un trivial i el poi és el trivial */
  let { poi, pregunta, itinerari } = RouterData.route.params;
  poi = pregunta ?? poi;

  const tempsRestant = useJocTeTemps(poi.segons, () => navigateToReward(0));
  const minuts = Math.floor(tempsRestant / 60);
  const segons = tempsRestant % 60;
  const tempsFormatat = `${minuts}:${segons.toString().padStart(2, "0")}`;

  const navigateToReward = (punts: number) => {
    if (RouterData.route.params.isTrivial) {
      return RouterData.navigation.replace("Trivial", {
        ...RouterData.route.params,
        poi: RouterData.route.params.trivialPoi,
        punts: (RouterData.route.params.punts || 0) + punts,
      });
    }
    RouterData.navigation.replace("Reward", {
      ...RouterData.route.params,
      punts,
    });
  };

  const injectedJavaScript = `
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function() {
        this.addEventListener('load', function() {
            if (this.responseURL === 'https://www.jigsawplanet.com/api/game/completed') {
                window.ReactNativeWebView.postMessage('finalitzat');
            }
        });
        originalSend.apply(this, arguments);
    };
    document.querySelector(".control").style.display = 'none'
    document.querySelector(".fa-share-alt").style.display = 'none'
    true;
  `;

  const onGameCompleted = () => {
    setTimeout(() => {
      playSoundForAWhile(imatgeRecurs("poi_encert_so"), undefined, () => {});
      setFullscreen(false);
      setShowCompletedFullscreen(true);
    }, 100);
  };

  const onContinueAfterCompleted = () => {
    setShowCompletedFullscreen(false);
    navigateToReward(poi.premi.punts);
  };

  const onWebViewMessage = () => {
    onGameCompleted();
  };

  return (
    <JocComponent RouterData={RouterData} setPistesDemanades={() => null}>
      {tempsRestant > 0 && (
        <View style={styles.tempsDeJocContainer}>
          <Image
            source={{ uri: imatgeRecurs("poi_reloj") }}
            style={{ width: 24, height: 24 }}
          />
          <Text style={{ fontSize: 18 }}>{tempsFormatat}</Text>
        </View>
      )}

      <View
        style={[
          styles.guessRow,
          {
            height: poi.url_360
              ? 400
              : Math.max(620, Dimensions.get("window").height * 0.65),
          },
        ]}
      >
        {poi.url_360 ? (
          <WebView
            source={{ uri: poi.url_360 }}
            injectedJavaScript={injectedJavaScript}
            onMessage={onWebViewMessage}
            allowFullScreen={true}
            style={{ height: 400, width: 400 }}
          />
        ) : (
          <>
            <View style={styles.puzzleActions}>
              <TouchableOpacity
                onPress={() => setFullscreen(true)}
                style={styles.puzzleActionButton}
                accessibilityLabel="Maximizar"
              >
                <FontAwesome5 name="expand" size={22} color="#333" />
              </TouchableOpacity>
            </View>
            <View style={[styles.puzzleWrapper, { margin: 20 }]}>
              <JigsawPuzzle
                imageSrc={poi.opcio_imatge1?.url}
                rows={2}
                columns={3}
                onSolved={onGameCompleted}
              />
            </View>
          </>
        )}
      </View>

      {!poi.url_360 && (
        <Modal
          visible={fullscreen}
          transparent
          animationType="fade"
          onRequestClose={() => setFullscreen(false)}
        >
          <View style={styles.fullscreenContainer}>
            <View style={styles.fullscreenActions}>
              {tempsRestant > 0 ? (
                <View style={styles.tempsDeJocContainer}>
                  <Image
                    source={{ uri: imatgeRecurs("poi_reloj") }}
                    style={{ width: 24, height: 24 }}
                  />
                  <Text style={{ fontSize: 18 }}>{tempsFormatat}</Text>
                </View>
              ) : (
                <View />
              )}
              <TouchableOpacity
                onPress={() => setFullscreen(false)}
                style={styles.fullscreenCloseButton}
                accessibilityLabel="Cerrar pantalla completa"
              >
                <FontAwesome5 name="compress" size={22} color="#333" />
              </TouchableOpacity>
            </View>
            <View style={styles.fullscreenPuzzleWrapper}>
              <JigsawPuzzle
                imageSrc={poi.opcio_imatge1?.url}
                rows={2}
                columns={3}
                onSolved={onGameCompleted}
              />
            </View>
          </View>
        </Modal>
      )}

      <Modal
        visible={showCompletedFullscreen}
        transparent
        animationType="fade"
        onRequestClose={onContinueAfterCompleted}
      >
        <View style={styles.fullscreenContainer}>
          {poi.opcio_imatge1?.url && (
            <Image
              source={{ uri: poi.opcio_imatge1.url }}
              style={styles.completedFullscreenImage}
              resizeMode="contain"
            />
          )}
          <TouchableOpacity
            onPress={onContinueAfterCompleted}
            style={styles.continueButton}
            accessibilityLabel={textRecurs("onboarding_continuar")}
          >
            <Text style={styles.continueButtonText}>
              {textRecurs("onboarding_continuar")}
            </Text>
          </TouchableOpacity>
        </View>
      </Modal>
    </JocComponent>
  );
}

const styles = StyleSheet.create({
  puzzleWrapper: {
    touchAction: "none",
    overflow: "hidden",
    flex: 1,
    minHeight: 380,
  },
  puzzleActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 8,
  },
  puzzleActionButton: {
    marginLeft: "auto",
    padding: 10,
  },
  fullscreenContainer: {
    flex: 1,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    height: Dimensions.get("window").height,
    width: Dimensions.get("window").width,
  },
  fullscreenActions: {
    position: "absolute",
    top: 50,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    zIndex: 10,
  },
  fullscreenCloseButton: {
    padding: 12,
    backgroundColor: "rgba(255,255,255,0.9)",
    borderRadius: 12,
  },
  fullscreenPuzzleWrapper: {
    flex: 1,
    width: Dimensions.get("window").width,
    minHeight: Dimensions.get("window").height - 120,
    touchAction: "none",
    overflow: "hidden",
    justifyContent: "center",
    padding: 20,
    alignSelf: "stretch",
  },
  guessRow: {
    alignContent: "flex-start",
    alignSelf: "flex-start",
    backgroundColor: "#f9f7e8",
    width: "100%",
    justifyContent: "flex-start",
    borderTopEndRadius: 44,
    borderTopStartRadius: 44,
    paddingVertical: 20,
  },
  tempsDeJocContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    backgroundColor: "white",
    borderRadius: 12,
    gap: 12,
    margin: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  completedFullscreenImage: {
    flex: 1,
    width: Dimensions.get("window").width,
    height: Dimensions.get("window").height,
  },
  continueButton: {
    position: "absolute",
    bottom: 50,
    alignSelf: "center",
    backgroundColor: constants.colorGreenDarkBtn,
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
  },
  continueButtonText: {
    color: constants.colorWhite,
    fontSize: 18,
  },
});
